export const COIN_PER_CASH = 10;

const EMPTY_FLOW_WINDOW = {
  deposits: { total: 0, count: 0 },
  withdrawals: { total: 0, count: 0 },
  net: 0,
};

export function normalizePaymentFlowWindow(raw) {
  if (!raw || typeof raw !== "object") {
    return { ...EMPTY_FLOW_WINDOW, deposits: { ...EMPTY_FLOW_WINDOW.deposits }, withdrawals: { ...EMPTY_FLOW_WINDOW.withdrawals } };
  }

  const deposits = {
    total: Number(raw.deposits?.total) || 0,
    count: Number(raw.deposits?.count) || 0,
  };
  const withdrawals = {
    total: Number(raw.withdrawals?.total) || 0,
    count: Number(raw.withdrawals?.count) || 0,
  };
  const net = Number.isFinite(Number(raw.net))
    ? Number(raw.net)
    : deposits.total - withdrawals.total;

  return { deposits, withdrawals, net };
}

export function normalizePaymentFlow(raw) {
  if (!raw) {
    return {
      allTime: normalizePaymentFlowWindow(),
      last24h: normalizePaymentFlowWindow(),
      last7d: normalizePaymentFlowWindow(),
    };
  }

  return {
    allTime: normalizePaymentFlowWindow(raw.allTime),
    last24h: normalizePaymentFlowWindow(raw.last24h),
    last7d: normalizePaymentFlowWindow(raw.last7d),
  };
}

export function paymentFlowNeedsFallback(raw) {
  return !raw || raw.allTime == null || raw.allTime.deposits == null;
}

export function coinToCashEquivalent(coin) {
  return (Number(coin) || 0) / COIN_PER_CASH;
}

export function calcTreasuryCoverage({
  providerBalance = 0,
  userCash = 0,
  lockedCash = 0,
  userCoin = 0,
  lockedCoin = 0,
} = {}) {
  const cashObligations = Number(userCash || 0) + Number(lockedCash || 0);
  const coinObligations =
    coinToCashEquivalent(userCoin) + coinToCashEquivalent(lockedCoin);
  const obligations = cashObligations + coinObligations;
  const gap = Number(providerBalance || 0) - obligations;
  const ratio = obligations > 0 ? providerBalance / obligations : null;
  let tone = "good";
  let label = "Covered";

  if (gap < 0) {
    tone = ratio != null && ratio >= 0.8 ? "warn" : "danger";
    label = ratio != null && ratio >= 0.8 ? "Thin cover" : "Funding gap";
  }

  return { obligations, cashObligations, coinObligations, gap, ratio, tone, label };
}

export function formatMoney(value, { currency = "RWF", compact = false } = {}) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";

  if (compact && Math.abs(num) >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(2)}M ${currency}`;
  }
  if (compact && Math.abs(num) >= 1_000) {
    return `${(num / 1_000).toFixed(1)}K ${currency}`;
  }

  return `${num.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} ${currency}`;
}

export function formatAmountParts(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return { value: "—", unit: "" };
  }

  return {
    value: num.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }),
    unit: "",
  };
}

export function formatLocalStamp(value, { short = false } = {}) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  if (short) {
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return date.toLocaleString();
}

export const ESCROW_ROWS = [
  { key: "onHold", label: "On hold", currency: "CASH" },
  { key: "p2pLocked", label: "P2P stakes", currency: "CASH" },
];

export function readEscrowRow(breakdown, key) {
  const row = breakdown?.[key];
  if (!row) return { total: 0, count: 0 };

  return {
    total: Number(row.total) || 0,
    count: row.count || 0,
  };
}

export function getActiveEscrowRows(breakdown) {
  return ESCROW_ROWS.map((row) => ({
    ...row,
    ...readEscrowRow(breakdown, row.key),
  })).filter((row) => row.total > 0 || row.count > 0);
}

export function buildLiabilityComposition({ wallets, escrow, coverage }) {
  const userCash = Number(wallets?.totalCash) || 0;
  const userCoinEq = coinToCashEquivalent(wallets?.totalCoin);
  const lockedCash = Number(escrow?.totalLockedCash) || 0;
  const lockedCoinEq = coinToCashEquivalent(escrow?.totalLockedCoin);
  const loans = Number(wallets?.totalUnpaidLoans) || 0;

  const segments = [
    { id: "user-cash", label: "User CASH", value: userCash, tone: "cash" },
    { id: "user-coin", label: "User COIN (eq.)", value: userCoinEq, tone: "coin" },
    { id: "locked-cash", label: "Locked CASH", value: lockedCash, tone: "locked" },
    { id: "locked-coin", label: "Locked COIN (eq.)", value: lockedCoinEq, tone: "locked-coin" },
  ];

  if (loans > 0) {
    segments.push({ id: "loans", label: "Unpaid loans", value: loans, tone: "warn" });
  }

  const total = Number(coverage?.obligations) || segments.reduce((sum, row) => sum + row.value, 0);

  return segments
    .filter((row) => row.value > 0)
    .map((row) => ({
      ...row,
      pct: total > 0 ? (row.value / total) * 100 : 0,
    }))
    .sort((a, b) => b.value - a.value);
}

export function buildEscrowAnalytics(breakdown) {
  const rows = getActiveEscrowRows(breakdown);
  const max = Math.max(1, ...rows.map((row) => row.total));
  return rows.map((row) => ({
    ...row,
    cashEq: row.currency === "COIN" ? coinToCashEquivalent(row.total) : row.total,
    pct: (row.total / max) * 100,
  }));
}

export function buildFlowWindows(paymentFlow) {
  const flow = normalizePaymentFlow(paymentFlow);
  return [
    { id: "24h", label: "24 hours", ...flow.last24h },
    { id: "7d", label: "7 days", ...flow.last7d },
    { id: "all", label: "All time", ...flow.allTime },
  ].map((window) => {
    const inflow = window.deposits?.total || 0;
    const outflow = window.withdrawals?.total || 0;
    const max = Math.max(inflow, outflow, 1);
    return {
      ...window,
      inflow,
      outflow,
      inflowPct: (inflow / max) * 100,
      outflowPct: (outflow / max) * 100,
      inCount: window.deposits?.count || 0,
      outCount: window.withdrawals?.count || 0,
    };
  });
}

export function buildFloatHistorySeries(history = []) {
  const chronological = [...history].reverse();
  const max = Math.max(1, ...chronological.map((row) => Number(row.balance) || 0));
  return chronological.map((row, index) => {
    const prev = chronological[index - 1];
    const balance = Number(row.balance) || 0;
    const delta = prev != null ? balance - (Number(prev.balance) || 0) : null;
    return {
      ...row,
      balance,
      delta,
      heightPct: (balance / max) * 100,
    };
  });
}

export function treasuryToneClass(tone) {
  if (tone === "danger") return "atrs-tone-danger";
  if (tone === "warn") return "atrs-tone-warn";
  return "atrs-tone-good";
}
